import { supabase } from '../config/supabase';
import { Property } from '../types/user';

interface PropertyImportData {
  name: string;
  address: string;
  city: string;
  postal_code?: string;
  description?: string;
  price?: number;
  size?: number;
  bedrooms?: number;
  bathrooms?: number;
  status?: string;
  type?: string;
}

export const importProperties = async (data: PropertyImportData[]): Promise<void> => {
  if (!data || data.length === 0) {
    throw new Error('No hay datos para importar');
  }

  // Validar los datos antes de importar
  const validatedData = data.map((item, index) => {
    // Validar campos obligatorios
    if (!item.name) {
      throw new Error(`Fila ${index + 1}: El nombre de la propiedad es obligatorio`);
    }
    if (!item.address) {
      throw new Error(`Fila ${index + 1}: La dirección es obligatoria`);
    }
    if (!item.city) {
      throw new Error(`Fila ${index + 1}: La ciudad es obligatoria`);
    }

    // Convertir campos numéricos
    const price = item.price ? Number(item.price) : null;
    const size = item.size ? Number(item.size) : null;
    const bedrooms = item.bedrooms ? Number(item.bedrooms) : null;
    const bathrooms = item.bathrooms ? Number(item.bathrooms) : null;

    // Validar que los campos numéricos sean válidos
    if (item.price && isNaN(price!)) {
      throw new Error(`Fila ${index + 1}: El precio debe ser un número válido`);
    }
    if (item.size && isNaN(size!)) {
      throw new Error(`Fila ${index + 1}: El tamaño debe ser un número válido`);
    }
    if (item.bedrooms && isNaN(bedrooms!)) {
      throw new Error(`Fila ${index + 1}: El número de habitaciones debe ser un número válido`);
    }
    if (item.bathrooms && isNaN(bathrooms!)) {
      throw new Error(`Fila ${index + 1}: El número de baños debe ser un número válido`);
    }

    // Devolver objeto con los datos validados
    return {
      name: item.name,
      address: item.address,
      city: item.city,
      postal_code: item.postal_code || null,
      description: item.description || null,
      price: price,
      size: size,
      bedrooms: bedrooms,
      bathrooms: bathrooms,
      status: item.status || 'available',
      type: item.type || 'apartment',
      created_at: new Date().toISOString(),
    };
  });

  // Insertar datos en la base de datos
  const { error } = await supabase
    .from('properties')
    .insert(validatedData);

  if (error) {
    console.error('Error al importar propiedades:', error);
    throw new Error(`Error al importar propiedades: ${error.message}`);
  }
};

// Función para obtener los campos de la plantilla de propiedades
export const getPropertyTemplateFields = (): string[] => {
  return [
    'name',
    'address',
    'city',
    'postal_code',
    'description',
    'price',
    'size',
    'bedrooms',
    'bathrooms',
    'status',
    'type'
  ];
};

// Función para exportar propiedades a Excel
export const exportPropertiesToExcel = async (): Promise<Property[]> => {
  const { data, error } = await supabase
    .from('properties')
    .select('*');

  if (error) {
    console.error('Error al exportar propiedades:', error);
    throw new Error(`Error al exportar propiedades: ${error.message}`);
  }

  return data || [];
};

// Función para hacer una copia de seguridad de todas las tablas importantes
export const backupDatabase = async (): Promise<Record<string, any[]>> => {
  try {
    // Lista de tablas a respaldar
    const tables = ['users', 'properties', 'monthly_stats'];
    const backup: Record<string, any[]> = {};

    // Obtener datos de cada tabla
    for (const table of tables) {
      const { data, error } = await supabase
        .from(table)
        .select('*');

      if (error) {
        throw new Error(`Error al respaldar tabla ${table}: ${error.message}`);
      }

      backup[table] = data || [];
    }

    return backup;
  } catch (error: any) {
    console.error('Error al hacer copia de seguridad:', error);
    throw new Error(`Error al hacer copia de seguridad: ${error.message}`);
  }
};
